import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Target, Eye, Award, Users, Globe, TrendingUp,
  Sparkles, ArrowRight, ExternalLink, GraduationCap,
  ShieldCheck, Star, Trophy, X
} from 'lucide-react';

// Real Image Imports
import NeerajImg from '../../img/Neeraj.jpeg';
import NitinImg from '../../img/Nitin.jpeg';
import GarimaImg from '../../img/Garima.jpeg';
import KomelImg from '../../img/Komel.jpeg';
import ShrutiImg from '../../img/Shruti_Bhasin.jpeg';

// Import Theme Context
import { useTheme } from '../Home/ThemeContext';

export default function AboutPage() {
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();
  const [isHovering, setIsHovering] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);

  // ========= LIVE COUNTER & PERSISTENCE ENGINE (Matches Frontpage Logic) =========
  const [counts, setCounts] = useState({
    students: 10000,
    universities: 500,
    countries: 50,
    success: 97
  });

  useEffect(() => {
    window.scrollTo(0, 0);

    // 1. Load persisted data from localStorage
    const savedData = localStorage.getItem('vidyazon_stats');
    const now = new Date().getTime();

    if (savedData) {
      const parsed = JSON.parse(savedData);
      // Check if data is within the 7-day window
      if (now - parsed.timestamp < 7 * 24 * 60 * 60 * 1000) {
        setCounts(prev => ({
          ...prev,
          students: parsed.students || 10000,
          universities: parsed.universities || 500 
        }));
      }
    }

    // 2. Continuous Random Growth Engine (Updates every 5 seconds)
    const growthInterval = setInterval(() => {
      setCounts(prev => {
        const newStudents = prev.students + Math.floor(Math.random() * 3) + 1;
        const newUnis = prev.universities + (Math.random() > 0.9 ? 1 : 0);
        
        const updatedStats = {
          students: newStudents,
          universities: newUnis,
          timestamp: new Date().getTime()
        };

        localStorage.setItem('vidyazon_stats', JSON.stringify(updatedStats));
        return { ...prev, students: newStudents, universities: newUnis };
      });
    }, 5000);

    return () => clearInterval(growthInterval);
  }, []);

  // Dynamic Styles Helpers
  const themeStyles = {
    bgMain: isDarkMode ? 'bg-[#0f172a]' : 'bg-slate-50',
    textPrimary: isDarkMode ? 'text-white' : 'text-slate-900',
    textSecondary: isDarkMode ? 'text-slate-400' : 'text-slate-600',
    cardBg: isDarkMode ? 'bg-slate-800/50 backdrop-blur-md border border-slate-700/50' : 'bg-white/80 backdrop-blur-md border border-white shadow-xl',
    sectionBgAlt: isDarkMode ? 'bg-slate-900/50' : 'bg-slate-100/50',
    accentGradient: 'from-indigo-600 via-purple-600 to-pink-600',
  };

  const statsData = [
    { value: counts.students.toLocaleString() + '+', label: 'Happy Students', icon: Users, color: 'text-blue-500' },
    { value: counts.universities + '+', label: 'Partner Universities', icon: Globe, color: 'text-purple-500' },
    { value: counts.countries + '+', label: 'Countries', icon: Globe, color: 'text-emerald-500' },
    { value: counts.success + '%', label: 'Success Rate', icon: TrendingUp, color: 'text-orange-500' }
  ];

  const values = [
    { icon: GraduationCap, title: 'Student-First Approach', description: 'Every decision we make prioritizes student success and satisfaction' },
    { icon: ShieldCheck, title: 'Trust & Transparency', description: 'Honest guidance and clear communication throughout your journey' },
    { icon: Star, title: 'Excellence in Service', description: 'Committed to delivering the highest quality educational consulting' },
    { icon: Trophy, title: 'Proven Track Record', description: 'Years of successful placements in top universities worldwide' }
  ];

  const team = [
    {
      name: 'Nitin Arora',
      role: 'IT Head',
      image: NitinImg,
      shortBio: 'Driving the digital transformation of Vidyazon with seamless tech-driven solutions.',
      fullBio: `Nitin Arora is the visionary IT Head at Vidyazon, spearheading our technological evolution and digital transformation strategy. With expertise in cloud infrastructure, database management, and scalable application architecture, Nitin ensures that Vidyazon's platforms are robust, secure, and user-centric.

His focus is on creating seamless, tech-driven solutions that simplify the student application process and strengthen university integrations. Nitin leads a talented team of developers and engineers who work tirelessly to deliver cutting-edge features that enhance the user experience.

Under his leadership, Vidyazon has successfully implemented advanced automation systems, real-time analytics dashboards, and AI-powered recommendation engines. He is passionate about leveraging emerging technologies to solve complex problems in the education sector.

Nitin's technical acumen combined with his understanding of educational challenges makes him an invaluable asset to the Vidyazon leadership team. He believes that technology should be a bridge between aspirations and opportunities.`,
      achievements: [
        'Architected scalable cloud infrastructure serving 10,000+ users',
        'Implemented AI-powered student matching algorithms',
        'Led digital transformation reducing processing time by 60%',
        'Established robust security and data privacy frameworks'
      ],
      expertise: ['Cloud Infrastructure', 'Full-Stack Development', 'System Architecture', 'AI/ML Integration']
    },
    {
      name: 'Neeraj Arora',
      role: 'Marketing Head',
      image: NeerajImg,
      shortBio: 'Strategic visionary connecting students with world-class opportunities globally.',
      fullBio: `Neeraj Arora brings 18+ years of strategic leadership and marketing excellence to Vidyazon as our Marketing Head. With a deep understanding of the education sector dynamics and global markets, Neeraj has positioned Vidyazon as a thought leader in international education services.

His strategic vision encompasses brand building, market penetration, and sustainable growth. Neeraj has successfully expanded Vidyazon's presence across multiple geographies, establishing strong partnerships with universities worldwide and building a robust network of education consultants.

He is a firm believer in data-driven marketing and customer-centric strategies. His approach integrates digital marketing, content strategy, and grassroots engagement to reach students at every touchpoint of their educational journey.

Neeraj's passion for education excellence and his ability to translate market insights into actionable strategies have been instrumental in Vidyazon's growth. He is committed to making quality education accessible to every aspirant, regardless of their background.`,
      achievements: [
        'Expanded market presence to 50+ countries',
        'Built strategic partnerships with 500+ universities',
        'Increased brand awareness by 300% in 3 years',
        'Developed comprehensive digital marketing ecosystem',
        'Mentored 50+ education consultants'
      ],
      expertise: ['Strategic Marketing', 'Brand Building', 'Market Expansion', 'Partnership Development', 'Digital Marketing']
    },
    {
      name: 'Garima Arora',
      role: 'Operational & Financial Head',
      image: GarimaImg,
      shortBio: 'Ensuring 100% transparent operations with precision in admissions and finance.',
      fullBio: `Garima Arora is the backbone of Vidyazon's operations and financial management, serving as our Operational & Financial Head. With meticulous attention to detail and a commitment to absolute transparency, Garima oversees the complex logistics of international admissions and financial planning.

Her expertise spans operations management, financial planning, compliance, and process optimization. Garima ensures that every student's journey through Vidyazon is smooth, transparent, and supported by robust operational systems.

She has implemented comprehensive tracking systems, financial auditing protocols, and quality assurance frameworks that guarantee accountability at every step. Her operational innovations have reduced processing times by 40% while maintaining the highest standards of accuracy and transparency.

Garima's financial acumen ensures that Vidyazon's growth is sustainable and fiscally responsible. She believes that operational excellence is the foundation of student satisfaction and organizational success.`,
      achievements: [
        'Implemented 100% transparent financial tracking systems',
        'Reduced operational processing time by 40%',
        'Established comprehensive compliance and audit frameworks',
        'Managed financial planning for 500+ partner institutions',
        'Achieved zero errors in 10,000+ processed applications'
      ],
      expertise: ['Operations Management', 'Financial Planning', 'Compliance & Auditing', 'Process Optimization', 'Risk Management']
    },
    {
      name: 'Komel Chadha',
      role: 'Senior Psychologist-Counsellor',
      image: KomelImg,
      shortBio: 'Providing essential career counseling and mental wellness support for global adaptation.',
      fullBio: `Komel Chadha is our Senior Psychologist and Counsellor at Vidyazon, dedicated to the holistic development and well-being of our students. With advanced training in educational psychology, career counseling, and cross-cultural adaptation, Komel provides comprehensive mental health and psychological support.

Her expertise covers student behavior analysis, career guidance, stress management, and cultural integration support. Komel understands the unique challenges international students face—from homesickness to academic pressure to cultural adjustment—and provides compassionate, evidence-based interventions.

She has developed proprietary psychological assessment tools and counseling programs that help students identify their strengths, manage stress, and thrive in international environments. Komel believes that mental wellness is as important as academic excellence.

Her compassionate approach combined with psychological expertise has helped thousands of students navigate their educational journeys with confidence and resilience. She is passionate about creating a supportive community where every student feels valued and understood.`,
      achievements: [
        'Developed comprehensive psychological assessment framework',
        'Provided counseling to 5,000+ international students',
        'Created stress management and wellness programs',
        'Implemented cultural integration support systems',
        '95% student satisfaction rating in mental wellness programs'
      ],
      expertise: ['Educational Psychology', 'Career Counseling', 'Stress Management', 'Cultural Adaptation', 'Mental Health Support']
    },
    {
      name: 'Dr. Shruti Bhasin',
      role: 'Chief Growth & Strategy Leader',
      image: ShrutiImg,
      shortBio: 'Seasoned growth strategist specializing in EdTech scaling and market expansion.',
      fullBio: `Dr. Shruti Bhasin is a seasoned growth and strategy leader with over 20 years of experience across education, consulting, and investments. As our Chief Growth & Strategy Leader, she specializes in building scalable growth engines for EdTech and education-driven organizations, with a strong focus on admissions, market expansion, and revenue acceleration.

She brings deep expertise in designing end-to-end student acquisition and conversion ecosystems—from lead generation and digital funnels to counseling frameworks and enrollment optimization. Her approach integrates consumer psychology, data-driven decision-making, and on-ground execution, ensuring measurable growth outcomes.

Dr. Shruti has worked closely with leadership teams to unlock new markets, build high-performing admission systems, and strengthen brand positioning in highly competitive education landscapes. She is particularly skilled at aligning marketing, counseling, and operations into a single, outcome-driven growth engine.

With an early career foundation in investments, she adds strong commercial acumen to her role—ensuring growth strategies are not just aggressive but also financially viable and scalable. She has successfully scaled multiple education organizations from startup to market leadership positions.

Beyond business strategy, Dr. Shruti is an accomplished author of "The Art of Staying Calm" and "The Calm Reset Playbook." She actively engages in thought leadership through podcasts and speaking engagements focused on mental clarity, resilience, and human-centered leadership—bringing a balanced and holistic perspective to organizational growth.`,
      achievements: [
        'Built scalable growth engines for 10+ EdTech organizations',
        'Scaled admissions systems handling 50,000+ applications annually',
        'Expanded market presence across 15+ new geographies',
        'Increased revenue by 250% through strategic growth initiatives',
        'Published 2 bestselling books on personal and organizational resilience',
        'Established thought leadership through 100+ podcast episodes'
      ],
      expertise: ['Growth Strategy', 'Market Expansion', 'EdTech Scaling', 'Admissions Systems', 'Revenue Acceleration', 'Consumer Psychology', 'Investment Strategy', 'Leadership Development']
    }
  ];

  // Duplicate team array for infinite scroll effect
  const extendedTeam = [...team, ...team];

  const milestones = [
    { year: '2018', title: 'Founded', description: 'Vidyazon began with a vision to innovate education services and empower student success.' },
    { year: '2020', title: 'Research & Foundation', description: 'Extensive research and surveys on education needs laid the foundation for a comprehensive educational platform.' },
    { year: '2023', title: 'AESPL eduZone Launch', description: 'Launched online coaching, preparation, and training & certification through AESPL eduZone.', link: 'https://aespleduzone.com/' },
    { year: '2025', title: 'Enhanced Integration', description: 'Integrated admission support and expert counselling into our platform, enhancing student outcomes.' }
  ];

  return (
    <div className={`min-h-screen transition-colors duration-500 ${themeStyles.bgMain}`}>
      
      {/* Unique Hero Section */}
      <div className="relative pt-32 pb-32 overflow-hidden">
        <div className={`absolute inset-0 bg-gradient-to-br ${themeStyles.accentGradient} opacity-90`}></div>
        <div className="absolute top-0 left-0 w-full h-full overflow-hidden z-0">
            <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] rounded-full bg-white/20 blur-[100px] animate-pulse"></div>
            <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] rounded-full bg-indigo-400/30 blur-[100px]"></div>
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 backdrop-blur-md border border-white/20 text-white text-sm mb-8 animate-bounce">
            <Sparkles className="w-4 h-4" />
            <span>Redefining Global Education</span>
          </div>
          
          <h1 className="text-5xl md:text-7xl font-black text-white mb-6 tracking-tight">
            Empowering Dreams,
            <span className="block text-transparent bg-clip-text bg-gradient-to-r from-yellow-200 to-orange-200">
                Transforming Futures
            </span>
          </h1>
          
          <p className="text-xl text-white/80 max-w-2xl mx-auto leading-relaxed">
            India's most innovative partner in international education, bridging the gap between local talent and global opportunities.
          </p>
        </div>
      </div>

      {/* Modern Stats Grid - SYNCED WITH FRONTPAGE COOKIE/LOCALSTORAGE */}
      <div className="max-w-7xl mx-auto px-4 -mt-12 relative z-20">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-8">
          {statsData.map((stat, idx) => {
            const Icon = stat.icon;
            return (
              <div key={idx} className={`${themeStyles.cardBg} p-8 rounded-[2rem] text-center group transition-all duration-500 hover:shadow-2xl hover:scale-105`}>
                <div className={`w-12 h-12 bg-gray-500/5 rounded-xl flex items-center justify-center mx-auto mb-4 group-hover:bg-indigo-500 transition-colors`}>
                    <Icon className={`w-6 h-6 ${stat.color} group-hover:text-white transition-colors`} />
                </div>
                <h3 className={`text-3xl font-black ${themeStyles.textPrimary} font-mono`}>{stat.value}</h3>
                <p className={`${themeStyles.textSecondary} text-[10px] font-black uppercase tracking-[0.2em] mt-2`}>{stat.label}</p>
              </div>
            );
          })}
        </div>
      </div>

      {/* Mission & Vision Section */}
      <div className="max-w-7xl mx-auto px-4 py-24">
        <div className="grid lg:grid-cols-2 gap-12">
          {/* Mission */}
          <div className="relative group">
            <div className="absolute inset-0 bg-sky-200 rounded-3xl rotate-2 group-hover:rotate-0 transition-transform duration-500 opacity-50"></div>
            <div className={`relative ${themeStyles.cardBg} p-10 rounded-3xl h-full shadow-lg`}>
              <Target className="w-12 h-12 text-sky-600 mb-6" />
              <h2 className={`text-3xl font-bold mb-4 ${themeStyles.textPrimary}`}>Our Mission</h2>
              <p className={`${themeStyles.textSecondary} leading-relaxed text-lg`}>
                Our mission is to empower students across the globe with clarity and confidence in their academic journeys. We deliver personalized admission support, expert academic counselling, focused coaching, and industry-relevant training and certifications—simplifying complex education decisions and enabling long-term success.
              </p>
            </div>
          </div>

          {/* Vision */}
          <div className="relative group">
            <div className="absolute inset-0 bg-blue-200 rounded-3xl -rotate-2 group-hover:rotate-0 transition-transform duration-500 opacity-50"></div>
            <div className={`relative ${themeStyles.cardBg} p-10 rounded-3xl h-full shadow-lg`}>
              <Eye className="w-12 h-12 text-blue-600 mb-6" />
              <h2 className={`text-3xl font-bold mb-4 ${themeStyles.textPrimary}`}>Our Vision</h2>
              <p className={`${themeStyles.textSecondary} leading-relaxed text-lg`}>
                Our vision is to become a globally trusted educational services platform that transforms student journeys through excellence in admissions, holistic counselling, and skill-based development—bridging aspirations with real opportunities and preparing students to thrive in a rapidly evolving world.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Core Values */}
      <div className={`py-24 ${themeStyles.sectionBgAlt}`}>
        <div className="max-w-7xl mx-auto px-4">
          <div className="mb-16">
            <h2 className={`text-4xl font-black ${themeStyles.textPrimary}`}>Values That Define Us</h2>
            <p className={`${themeStyles.textSecondary} mt-2`}>The foundation of our student success stories.</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value, idx) => {
              const Icon = value.icon;
              return (
                <div key={idx} className={`${themeStyles.cardBg} p-8 rounded-3xl hover:border-indigo-500 transition-all duration-300`}>
                  <Icon className="w-10 h-10 text-indigo-500 mb-6" />
                  <h3 className={`text-xl font-bold mb-3 ${themeStyles.textPrimary}`}>{value.title}</h3>
                  <p className={themeStyles.textSecondary}>{value.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Journey Timeline */}
      <div className="max-w-7xl mx-auto px-4 py-24">
        <h2 className={`text-4xl font-black text-center mb-20 ${themeStyles.textPrimary}`}>Our Evolution</h2>
        <div className="space-y-12">
          {milestones.map((item, idx) => (
            <div key={idx} className="flex flex-col md:flex-row gap-8 items-start">
              <div className="flex flex-row md:flex-col items-center gap-4">
                <span className="text-5xl font-black text-indigo-600/20">{item.year}</span>
                <div className="h-1 md:h-24 w-24 md:w-1 bg-gradient-to-b from-indigo-500 to-transparent rounded-full"></div>
              </div>
              <div className={`${themeStyles.cardBg} flex-1 p-8 rounded-3xl`}>
                <h3 className={`text-2xl font-bold mb-2 ${themeStyles.textPrimary}`}>{item.title}</h3>
                <p className={`${themeStyles.textSecondary} mb-4`}>{item.description}</p>
                {item.link && (
                  <a href={item.link} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 text-indigo-500 font-bold hover:underline">
                    Visit Site <ExternalLink className="w-4 h-4" />
                  </a>
                )}
              </div>
            </div>
          ))}
          <div className="p-8 rounded-3xl bg-gradient-to-r from-indigo-600 to-purple-600 text-white mt-8 shadow-xl">
            <p className="text-lg font-medium italic">
              "Today, Vidyazon uniquely blends coaching, certification, admissions, and counselling to support learners at every stage of their educational journey."
            </p>
          </div>
        </div>
      </div>

      {/* ===== CONTINUOUS MOVING CAROUSEL - FULL PAGE ===== */}
      <style>{`
        @keyframes scroll-left {
          0% {
            transform: translateX(0);
          }
          100% {
            transform: translateX(calc(-280px * 5));
          }
        }

        @keyframes scroll-left-md {
          0% {
            transform: translateX(0);
          }
          100% {
            transform: translateX(calc(-320px * 5));
          }
        }

        @keyframes scroll-left-lg {
          0% {
            transform: translateX(0);
          }
          100% {
            transform: translateX(calc(-340px * 5));
          }
        }

        .carousel-track-sm {
          animation: scroll-left 50s linear infinite;
        }

        .carousel-track-md {
          animation: scroll-left-md 50s linear infinite;
        }

        .carousel-track-lg {
          animation: scroll-left-lg 50s linear infinite;
        }

        .carousel-track-sm.paused,
        .carousel-track-md.paused,
        .carousel-track-lg.paused {
          animation-play-state: paused;
        }

        .carousel-container:hover .carousel-track-sm,
        .carousel-container:hover .carousel-track-md,
        .carousel-container:hover .carousel-track-lg {
          animation-play-state: paused;
        }
      `}</style>

      <div className={`min-h-screen flex flex-col items-center justify-center py-24 ${themeStyles.sectionBgAlt} overflow-hidden`}>
        <div className="max-w-7xl mx-auto px-4 w-full mb-16">
          <div className="text-center">
            <h2 className={`text-4xl font-black mb-4 ${themeStyles.textPrimary}`}>The Minds Behind Vidyazon</h2>
            <div className="w-24 h-1 bg-indigo-500 mx-auto rounded-full"></div>
            <p className={`${themeStyles.textSecondary} mt-4 text-lg`}>Continuous Excellence in Education</p>
          </div>
        </div>

        {/* Carousel Wrapper */}
        <div 
          className="carousel-container w-full overflow-hidden"
          onMouseEnter={() => setIsHovering(true)}
          onMouseLeave={() => setIsHovering(false)}
        >
          {/* Small Screens (1 column) */}
          <div className="block sm:hidden w-full">
            <div className="carousel-track-sm flex gap-6 w-max">
              {extendedTeam.map((member, idx) => (
                <div key={`sm-${idx}`} className="flex-shrink-0 w-[280px]">
                  <TeamCard member={member} themeStyles={themeStyles} onReadMore={() => setSelectedMember(member)} />
                </div>
              ))}
            </div>
          </div>

          {/* Medium Screens (2 columns) */}
          <div className="hidden sm:block md:hidden w-full">
            <div className="carousel-track-md flex gap-6 w-max">
              {extendedTeam.map((member, idx) => (
                <div key={`md-${idx}`} className="flex-shrink-0 w-[320px]">
                  <TeamCard member={member} themeStyles={themeStyles} onReadMore={() => setSelectedMember(member)} />
                </div>
              ))}
            </div>
          </div>

          {/* Large Screens (4 columns) */}
          <div className="hidden md:block w-full">
            <div className="carousel-track-lg flex gap-6 w-max">
              {extendedTeam.map((member, idx) => (
                <div key={`lg-${idx}`} className="flex-shrink-0 w-[340px]">
                  <TeamCard member={member} themeStyles={themeStyles} onReadMore={() => setSelectedMember(member)} />
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Hover Indicator */}
        <div className="mt-12 text-center">
          <p className={`${themeStyles.textSecondary} text-sm`}>
            {isHovering ? '⏸️  Paused' : '▶️ Continuous '}
          </p>
        </div>
      </div>

      {/* ===== MODAL POPUP ===== */}
      {selectedMember && (
        <MemberModal 
          member={selectedMember} 
          themeStyles={themeStyles} 
          onClose={() => setSelectedMember(null)}
        />
      )}

      {/* Modern CTA */}
      <div className="max-w-7xl mx-auto px-4 py-20">
        <div className="relative rounded-[3rem] overflow-hidden bg-slate-900 p-12 md:p-20 text-center">
            <div className="absolute inset-0 bg-gradient-to-r from-indigo-600/20 to-pink-600/20"></div>
            <div className="relative z-10">
                <h2 className="text-3xl md:text-5xl font-black text-white mb-6">Ready to start your journey?</h2>
                <p className="text-slate-400 text-lg mb-10 max-w-xl mx-auto">Join thousands of students who have already transformed their lives through Vidyazon's expert guidance.</p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                    <button onClick={() => navigate('/contact')} className="px-10 py-4 bg-indigo-600 text-white font-bold rounded-2xl hover:bg-indigo-700 transition-all flex items-center justify-center gap-2 shadow-xl shadow-indigo-500/20">
                        Get Started Now <ArrowRight className="w-5 h-5" />
                    </button>
                    <button onClick={() => navigate('/services')} className="px-10 py-4 bg-white/5 text-white border border-white/10 backdrop-blur-md font-bold rounded-2xl hover:bg-white/10 transition-all">
                        View Services
                    </button>
                </div>
            </div>
        </div>
      </div>
    </div>
  );
}

// Team Card Component
function TeamCard({ member, themeStyles, onReadMore }) {
  return (
    <div className="group relative h-full">
      <div className="absolute inset-0 bg-indigo-500 rounded-[2.5rem] rotate-3 group-hover:rotate-6 transition-transform duration-300"></div>
      <div className={`${themeStyles.cardBg} relative rounded-[2.5rem] overflow-hidden transition-transform duration-300 group-hover:-translate-y-2 h-full flex flex-col`}>
        <div className="aspect-[4/5] overflow-hidden">
          <img src={member.image} alt={member.name} className="w-full h-full object-cover object-top filter grayscale group-hover:grayscale-0 transition-all duration-500" />
        </div>
        <div className="p-6 flex-grow flex flex-col">
          <h3 className={`text-xl font-bold ${themeStyles.textPrimary}`}>{member.name}</h3>
          <p className="text-indigo-500 text-xs font-black uppercase tracking-widest mb-4">{member.role}</p>
          <p className={`text-sm ${themeStyles.textSecondary} leading-relaxed flex-grow line-clamp-3`}>{member.shortBio}</p>
          <button
            onClick={onReadMore}
            className="mt-4 px-4 py-2 bg-indigo-600 text-white text-sm font-bold rounded-lg hover:bg-indigo-700 transition-all"
          >
            Read More →
          </button>
        </div>
      </div>
    </div>
  );
}

// Member Modal Component
function MemberModal({ member, themeStyles, onClose }) {
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, []);

  return (
    <div 
      className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto"
      onClick={onClose}
    >
      <div 
        className={`${themeStyles.cardBg} rounded-[3rem] max-w-3xl w-full my-8 relative`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-6 right-6 p-2 rounded-full bg-red-600 hover:bg-red-700 text-white z-10 transition-all"
        >
          <X className="w-6 h-6" />
        </button>

        {/* Modal Content */}
        <div className="grid md:grid-cols-2 gap-8 p-8 md:p-12">
          {/* Image */}
          <div className="flex items-center justify-center">
            <div className="relative">
              <div className="absolute inset-0 bg-indigo-500 rounded-[2rem] rotate-3"></div>
              <img 
                src={member.image} 
                alt={member.name} 
                className="relative w-full h-[400px] object-cover rounded-[2rem]"
              />
            </div>
          </div>

          {/* Content */}
          <div className="flex flex-col justify-start overflow-y-auto max-h-[500px]">
            <div className="mb-4">
              <h2 className={`text-3xl font-black ${themeStyles.textPrimary} mb-2`}>{member.name}</h2>
              <p className="text-indigo-500 text-sm font-black uppercase tracking-widest">{member.role}</p>
            </div>

            {/* Full Bio */}
            <div className={`${themeStyles.textSecondary} text-sm leading-relaxed mb-6 space-y-4`}>
              {member.fullBio.split('\n\n').map((paragraph, idx) => (
                <p key={idx}>{paragraph}</p>
              ))}
            </div>

            {/* Achievements */}
            {member.achievements && member.achievements.length > 0 && (
              <div className="mb-6">
                <h3 className={`text-lg font-bold ${themeStyles.textPrimary} mb-3`}>Key Achievements</h3>
                <ul className={`${themeStyles.textSecondary} text-sm space-y-2`}>
                  {member.achievements.map((achievement, idx) => (
                    <li key={idx} className="flex gap-3">
                      <span className="text-indigo-500 font-bold flex-shrink-0">✓</span>
                      <span>{achievement}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Expertise */}
            {member.expertise && member.expertise.length > 0 && (
              <div>
                <h3 className={`text-lg font-bold ${themeStyles.textPrimary} mb-3`}>Areas of Expertise</h3>
                <div className="flex flex-wrap gap-2">
                  {member.expertise.map((skill, idx) => (
                    <span 
                      key={idx}
                      className="px-3 py-1 bg-indigo-600/20 text-indigo-500 text-xs font-bold rounded-full"
                    >
                      {skill}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}